/**
 * @file Player dash on mouse2: brief burst of speed that stuns and disarms.
 *
 * Priority sits after {@link ./PlayerControlSystem.js} (which writes aim and
 * move intent) and before movement, so the dash can overwrite desired velocity
 * for its duration. First enemy swept during the dash is stunned and disarmed
 * the same way a thrown weapon would — no damage.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import {
  Transform,
  Velocity,
  PlayerControlled,
  Collider,
  Health,
  AIBrain,
  RangedAI
} from '../components/index.js';
import { Action } from '../input/InputManager.js';
import { Events } from '../core/EventBus.js';
import { getPlayerTuning } from '../config/PlayerTuning.js';
import { raycastAABB } from '../physics/AABB.js';
import { stunAndDisarm } from '../combat/stunDisarm.js';

export class DashSystem extends System {
  /** After player control (10), before throw (15) and movement (40). */
  static priority = 12;

  constructor() {
    super();
    /** @type {THREE.Vector3} @private */
    this._dir = new THREE.Vector3();
    /** @type {THREE.Vector3} @private */
    this._from = new THREE.Vector3();
    /** @type {object} @private */
    this._shakeEvent = { amount: 0 };
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    const { input, config } = this.ctx;
    const dashCfg = getPlayerTuning().dash ?? config.player.dash;
    if (!dashCfg) return;

    for (const entity of this.world.query(PlayerControlled, Transform, Velocity, Health)) {
      const health = entity.get(Health);
      if (health.dead) continue;

      const control = entity.get(PlayerControlled);
      const transform = entity.get(Transform);
      const velocity = entity.get(Velocity);

      if (control.dashCooldown > 0) {
        control.dashCooldown = Math.max(0, control.dashCooldown - dt);
      }

      if (control.dashRemaining > 0) {
        this._tickDash(entity, control, transform, velocity, health, dashCfg, dt);
        continue;
      }

      if (!input.justPressed(Action.DASH)) continue;
      if (control.dashCooldown > 0) continue;

      this._startDash(entity, control, transform, velocity, health, dashCfg);
    }
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity
   * @param {import('../components/PlayerControlled.js').PlayerControlled} control
   * @param {import('../components/Transform.js').Transform} transform
   * @param {import('../components/Velocity.js').Velocity} velocity
   * @param {import('../components/Health.js').Health} health
   * @param {object} dashCfg
   * @returns {void}
   * @private
   */
  _startDash(entity, control, transform, velocity, health, dashCfg) {
    const { input, events } = this.ctx;
    const move = input.getMoveVector({ x: 0, y: 0 });
    if (move.x !== 0 || move.y !== 0) {
      // Screen-relative move: same axes PlayerControlSystem uses.
      this._dir.set(move.x, 0, move.y).normalize();
    } else {
      this._dir.set(Math.sin(transform.rotationY), 0, Math.cos(transform.rotationY));
    }

    control.dashDirX = this._dir.x;
    control.dashDirZ = this._dir.z;
    control.dashRemaining = dashCfg.duration;
    control.dashCooldown = dashCfg.cooldown;
    control.dashHit = false;

    const speed = dashCfg.distance / Math.max(dashCfg.duration, 1e-4);
    velocity.maxSpeed = speed;
    velocity.desired.set(this._dir.x * speed, 0, this._dir.z * speed);
    velocity.linear.set(this._dir.x * speed, 0, this._dir.z * speed);

    if (dashCfg.invulnerability > 0) {
      health.invulnerableFor = Math.max(health.invulnerableFor, dashCfg.invulnerability);
    }

    if (dashCfg.shake > 0) {
      this._shakeEvent.amount = dashCfg.shake;
      events.emit(Events.SHAKE, this._shakeEvent);
    }

    events.emit(Events.PLAYER_DASHED, { entity });
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity
   * @param {import('../components/PlayerControlled.js').PlayerControlled} control
   * @param {import('../components/Transform.js').Transform} transform
   * @param {import('../components/Velocity.js').Velocity} velocity
   * @param {import('../components/Health.js').Health} health
   * @param {object} dashCfg
   * @param {number} dt
   * @returns {void}
   * @private
   */
  _tickDash(entity, control, transform, velocity, health, dashCfg, dt) {
    control.dashRemaining = Math.max(0, control.dashRemaining - dt);

    const speed = dashCfg.distance / Math.max(dashCfg.duration, 1e-4);
    velocity.maxSpeed = speed;
    velocity.desired.set(control.dashDirX * speed, 0, control.dashDirZ * speed);
    velocity.linear.set(control.dashDirX * speed, 0, control.dashDirZ * speed);

    if (!control.dashHit) {
      const hit = this._sweepHit(entity, transform, control, dashCfg);
      if (hit) {
        control.dashHit = true;
        stunAndDisarm(this.ctx, hit);
        // Impact cue already used by thrown weapons.
        this.ctx.events.emit(Events.BULLET_HIT, {
          position: hit.get(Transform).position,
          normal: { x: -control.dashDirX, y: 0, z: -control.dashDirZ },
          target: hit
        });
      }
    }
  }

  /**
   * Nearest enemy along this frame's dash segment.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Player.
   * @param {import('../components/Transform.js').Transform} transform
   * @param {import('../components/PlayerControlled.js').PlayerControlled} control
   * @param {object} dashCfg
   * @returns {import('../ecs/Entity.js').Entity | null}
   * @private
   */
  _sweepHit(entity, transform, control, dashCfg) {
    const from = transform.previousPosition;
    const dx = transform.position.x - from.x;
    const dz = transform.position.z - from.z;
    // If movement has not integrated yet this frame, probe a short segment along
    // the dash direction so a standing-start dash still connects.
    const len = Math.hypot(dx, dz);
    const probeDx = len > 1e-6 ? dx : control.dashDirX * dashCfg.distance * 0.15;
    const probeDz = len > 1e-6 ? dz : control.dashDirZ * dashCfg.distance * 0.15;
    const originX = len > 1e-6 ? from.x : transform.position.x;
    const originZ = len > 1e-6 ? from.z : transform.position.z;

    const playerCollider = entity.get(Collider);
    const half = (dashCfg.radius ?? playerCollider?.halfX ?? 0.6);

    let bestT = Infinity;
    /** @type {import('../ecs/Entity.js').Entity | null} */
    let best = null;

    for (const other of this._enemyCandidates()) {
      if (other === entity || !other.alive) continue;
      if (other.get(Health)?.dead) continue;

      const otherCollider = other.get(Collider);
      const otherTransform = other.get(Transform);
      if (!otherCollider || !otherTransform) continue;

      const t = raycastAABB(
        originX,
        originZ,
        probeDx,
        probeDz,
        otherTransform.position.x - otherCollider.halfX - half,
        otherTransform.position.z - otherCollider.halfZ - half,
        otherTransform.position.x + otherCollider.halfX + half,
        otherTransform.position.z + otherCollider.halfZ + half
      );
      if (t < 0 || t > 1 || t >= bestT) continue;
      bestT = t;
      best = other;
    }
    return best;
  }

  /**
   * @returns {import('../ecs/Entity.js').Entity[]}
   * @private
   */
  _enemyCandidates() {
    const classic = this.world.query(AIBrain, Transform, Collider);
    const ranged = this.world.query(RangedAI, Transform, Collider);
    if (classic.length === 0) return ranged;
    if (ranged.length === 0) return classic;

    const seen = new Set();
    const result = [];
    for (const entity of classic) {
      seen.add(entity.id);
      result.push(entity);
    }
    for (const entity of ranged) {
      if (seen.has(entity.id)) continue;
      result.push(entity);
    }
    return result;
  }
}
